// VerificationBanner — the incoming verification request.
//
// A request decides whether a device may read the account's messages, so
// it is a sheet, not a strip: on a phone it rises from the bottom edge
// above the tab bar, on a computer it is the same centred sheet the
// verification itself continues in. "Verify" promotes the request to the
// live `DeviceVerification` sheet; "Not now" and Escape decline it.
//
// One request at a time: a new incoming request while another is live
// replaces the previous one. The hook owns that policy.

import { Dialog } from "~/components/Dialog";
import {
  SheetHeader,
  VerificationGhostButton,
  VerificationPrimaryButton,
} from "~/components/verification-primitives";
import type { VerificationRequest } from "~/lib/matrix";

export interface VerificationBannerProps {
  request: VerificationRequest;
  /** The signed-in user: a request from this account is one of their devices. */
  ownUserId?: string | null;
  /** This device cannot read its history yet, so it is the one being verified. */
  thisDeviceLocked?: boolean;
  onAccept: () => void;
  onDismiss: () => void;
}

export function VerificationBanner({
  request,
  ownUserId = null,
  thisDeviceLocked = false,
  onAccept,
  onDismiss,
}: VerificationBannerProps) {
  const ownDevice = ownUserId !== null && request.otherUserId === ownUserId;
  const name = request.otherDisplayName ?? request.otherUserId;
  const title = !ownDevice
    ? `Verify ${name}`
    : thisDeviceLocked
      ? "Verify this device"
      : "New sign-in";
  const body = !ownDevice
    ? `${name} wants to verify you.`
    : thisDeviceLocked
      ? "Your other device wants to verify this one so it can read your messages."
      : "Your account was opened on another device. Verify it so it can read your messages.";
  return (
    <Dialog
      labelledBy="verification-request-title"
      onClose={onDismiss}
      dismissOnBackdrop={false}
      grabber={false}
      width="420px"
      panelStyle={{ maxWidth: "calc(100vw - var(--chat-space-4))" }}
    >
      <section
        aria-label="Incoming device verification"
        data-testid="verification-banner"
        style={{
          display: "flex",
          flexDirection: "column",
          gap: "var(--chat-space-6)",
          padding: "var(--chat-space-6) var(--chat-space-8) var(--chat-space-8)",
        }}
      >
        <SheetHeader id="verification-request-title" title={title} />
        <p
          style={{
            margin: 0,
            color: "var(--chat-fg-secondary)",
            fontFamily: "var(--chat-font-sans)",
            fontSize: "var(--chat-type-body-lg-size)",
            lineHeight: "var(--chat-type-body-lg-line)",
            textAlign: "center",
          }}
        >
          {body}
        </p>
        <div style={{ display: "flex", flexDirection: "column", gap: "var(--chat-space-2)" }}>
          <VerificationPrimaryButton size="md" block onClick={onAccept}>
            Verify
          </VerificationPrimaryButton>
          <VerificationGhostButton size="md" block onClick={onDismiss}>
            Not now
          </VerificationGhostButton>
        </div>
      </section>
    </Dialog>
  );
}
